import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ShieldOff, Send } from "lucide-react";
import { api } from "../../lib/api";
import AdminShareImagePanel from "./AdminShareImagePanel";
import AdminUrgentPhotoUpload from "./AdminUrgentPhotoUpload";
import AdminOutreachDraft from "./AdminOutreachDraft";

const OFFER_STATUSES = ["New", "Contacted", "Matched", "Declined"];
const TABS = [["photo", "Photo"], ["donations", "Donation links"], ["offers", "Help offers"], ["contact", "Contact rescues"], ["share", "Share image"]];

function Donations({ rescue, headers, onChanged }) {
  const toggle = async (i, verified) => {
    try { await api.post(`/admin/urgent-rescues/${rescue.id}/donations/${i}/${verified ? "unverify" : "verify"}`, {}, { headers }); onChanged(); }
    catch { toast.error("Update failed."); }
  };
  const links = rescue.donation_links || [];
  if (!links.length) return <p className="text-sm text-zinc-600">No donation links yet — add them via Edit.</p>;
  return (
    <ul className="space-y-2" data-testid="admin-donation-links">
      {links.map((d, i) => (
        <li key={`${d.url}-${i}`} className="flex items-center gap-3 border border-zinc-200 px-3 py-2 text-sm">
          <div className="flex-1 min-w-0"><b>{d.org_name}</b> <a href={d.url} target="_blank" rel="noreferrer" className="underline text-xs break-all">{d.url}</a></div>
          <button onClick={() => toggle(i, d.verified)} className={`text-xs px-2 py-1 inline-flex items-center gap-1 ${d.verified ? "bg-green-700 text-white" : "border border-zinc-900"}`} data-testid={`admin-donation-verify-${i}`}>
            {d.verified ? <><ShieldCheck size={12} /> Verified (undo)</> : <><ShieldOff size={12} /> Verify</>}
          </button>
        </li>
      ))}
    </ul>
  );
}

function Offers({ rescue, headers }) {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get(`/admin/urgent-rescues/${rescue.id}/help-offers`, { headers }).then(({ data }) => setItems(data.items)).catch(() => setItems([])), [rescue.id, headers]);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (o, status) => {
    try { await api.put(`/admin/urgent-rescues/help-offers/${o.id}`, { status, admin_note: o.admin_note || "" }, { headers }); load(); }
    catch { toast.error("Update failed."); }
  };
  if (!items) return <Loader2 size={14} className="animate-spin" />;
  if (!items.length) return <p className="text-sm text-zinc-600">No help offers yet.</p>;
  return (
    <ul className="space-y-2" data-testid="admin-help-offers">
      {items.map((o) => (
        <li key={o.id} className="border border-zinc-200 p-3 text-sm">
          <div className="flex items-center justify-between gap-2">
            <b>{o.name}{o.organization && ` · ${o.organization}`}</b>
            <select value={o.status} onChange={(e) => setStatus(o, e.target.value)} className="border border-zinc-300 text-xs px-1 py-0.5" data-testid={`admin-offer-status-${o.id}`}>{OFFER_STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
          </div>
          <div className="text-xs text-zinc-600">{o.role} · <a className="underline" href={`mailto:${o.email}`}>{o.email}</a> {o.phone && `· ${o.phone}`} {o.service_area && `· ${o.service_area}`} {o.availability && `· Avail: ${o.availability}`}</div>
          <p className="mt-1 whitespace-pre-line">{o.how_i_can_help}</p>
        </li>
      ))}
    </ul>
  );
}

function Contact({ rescue, headers, onChanged }) {
  const [f, setF] = useState({ include_rescue_partners: true, include_shelters: true, custom: "", extra_message: "" });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!window.confirm("Send this rescue alert by email to the selected rescues & shelters?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/urgent-rescues/${rescue.id}/contact-rescues`, {
        include_rescue_partners: f.include_rescue_partners, include_shelters: f.include_shelters,
        include_custom_emails: f.custom.split(/[\s,;]+/).filter(Boolean), extra_message: f.extra_message,
      }, { headers });
      if (data.note) toast.warning(`Targeted ${data.recipient_count} recipients — emails NOT sent: email service key not set yet.`);
      else toast.success(`Sent to ${data.sent_count} of ${data.recipient_count} recipients.`);
      onChanged();
    } catch { toast.error("Send failed."); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 text-sm max-w-xl" data-testid="admin-contact-rescues">
      <label className="flex items-center gap-2"><input type="checkbox" checked={f.include_rescue_partners} onChange={(e) => setF({ ...f, include_rescue_partners: e.target.checked })} data-testid="contact-include-partners" /> My Rescue Partners (same state)</label>
      <label className="flex items-center gap-2"><input type="checkbox" checked={f.include_shelters} onChange={(e) => setF({ ...f, include_shelters: e.target.checked })} data-testid="contact-include-shelters" /> Local Shelters directory (same state)</label>
      <input placeholder="Extra emails (comma separated)" value={f.custom} onChange={(e) => setF({ ...f, custom: e.target.value })} className="w-full border border-zinc-300 px-2 py-1.5" data-testid="contact-custom-emails" />
      <textarea rows={3} placeholder="Note to include (optional)" value={f.extra_message} onChange={(e) => setF({ ...f, extra_message: e.target.value })} className="w-full border border-zinc-300 px-2 py-1.5" data-testid="contact-extra-message" />
      <button onClick={send} disabled={busy} className="btn-danger disabled:opacity-60" data-testid="contact-send-btn">{busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Contact rescues</button>
      {rescue.last_contact_sent_at && <p className="text-xs text-zinc-500">Last sent {new Date(rescue.last_contact_sent_at).toLocaleString()} · {rescue.last_contact_targeted_count ?? "?"} targeted · {rescue.last_contact_recipient_count} delivered</p>}
    </div>
  );
}

export default function AdminUrgentRescueTools({ rescue, headers, onChanged }) {
  const [tab, setTab] = useState("photo");
  const props = { rescue, headers, onChanged };
  return (
    <div className="border-t border-zinc-200 bg-zinc-50 p-4" data-testid={`urgent-tools-${rescue.id}`}>
      <div className="flex flex-wrap gap-1 mb-4">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`text-[11px] uppercase tracking-widest font-bold px-3 py-1.5 border ${tab === k ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300"}`} data-testid={`urgent-tools-tab-${k}`}>{l}</button>
        ))}
      </div>
      {tab === "photo" && <AdminUrgentPhotoUpload {...props} />}
      {tab === "donations" && <Donations {...props} />}
      {tab === "offers" && <Offers {...props} />}
      {tab === "contact" && <><AdminOutreachDraft {...props} /><div className="mt-5"><Contact {...props} /></div></>}
      {tab === "share" && <AdminShareImagePanel {...props} />}
    </div>
  );
}
